// Offline Banner - Shows connection status and sync state
// Displays at top of screen when offline or syncing

import React from 'react';

/**
 * Offline status banner component
 * @param {object} props
 * @param {boolean} props.isOnline - Current online status
 * @param {boolean} props.isSyncing - Whether sync is in progress
 * @param {number} props.pendingCount - Number of pending events
 * @param {function} props.onSyncNow - Optional callback to trigger manual sync
 */
export function OfflineBanner({ isOnline, isSyncing, pendingCount, onSyncNow }) {
  // Don't show if online with nothing pending
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null;
  }

  // Determine banner state
  let backgroundColor, textColor, icon, message;

  if (!isOnline) {
    // Offline state
    backgroundColor = '#fef3c7'; // amber-100
    textColor = '#92400e'; // amber-800
    icon = '⚠️';
    message = pendingCount > 0
      ? `Offline — ${pendingCount} item${pendingCount > 1 ? 's' : ''} saved locally`
      : 'Offline — changes will sync when back online';
  } else if (isSyncing) {
    // Syncing state
    backgroundColor = '#dbeafe'; // blue-100
    textColor = '#1e40af'; // blue-800
    icon = '↻';
    message = `Syncing ${pendingCount} item${pendingCount > 1 ? 's' : ''}...`;
  } else if (pendingCount > 0) {
    // Online with pending items (sync will happen automatically)
    backgroundColor = '#dcfce7'; // green-100
    textColor = '#166534'; // green-800
    icon = '↑';
    message = `${pendingCount} item${pendingCount > 1 ? 's' : ''} ready to sync`;
  }

  return (
    <div
      style={{
        backgroundColor,
        color: textColor,
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '13px',
        fontWeight: '500',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            fontSize: '14px',
            animation: isSyncing ? 'spin 1s linear infinite' : 'none',
            display: 'inline-block',
          }}
        >
          {icon}
        </span>
        <span>{message}</span>
      </div>

      {/* Sync Now button - only show when online with pending items and not already syncing */}
      {isOnline && pendingCount > 0 && !isSyncing && onSyncNow && (
        <button
          onClick={onSyncNow}
          style={{
            background: 'none',
            border: `1px solid ${textColor}`,
            borderRadius: '6px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: '600',
            color: textColor,
            cursor: 'pointer',
          }}
        >
          Sync Now
        </button>
      )}

      {/* Spinner animation */}
      {isSyncing && (
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      )}
    </div>
  );
}

/**
 * Pending count badge for action tiles
 * @param {object} props
 * @param {number} props.count - Number of pending items
 */
export function PendingBadge({ count }) {
  if (!count || count === 0) return null;

  return (
    <span
      style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        backgroundColor: '#f59e0b', // amber-500
        color: 'white',
        fontSize: '11px',
        fontWeight: '700',
        minWidth: '20px',
        height: '20px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 6px',
      }}
    >
      {count}
    </span>
  );
}

export default OfflineBanner;
